import { PropertyMetaEnum, type PropertyTypeEnum } from '@salescore/client-api'
import type { CoreModel, ModelProperty } from '@salescore/core'
import { type SalescoreObjectPolicyAction, salescoreObjectPolicyActionSchema } from '@salescore/features'
import type { Rule } from 'antd/es/form'
import { Array } from 'effect'
import { isNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { z } from 'zod'

interface FormPropertyTypeData {
  type: PropertyTypeEnum
  // ModelProperty だと meta は nullish() だから null も許容する
  meta: PropertyMetaEnum | null | undefined
}

/**
 * カスタムオブジェクトの各項目（フォーム値）
 * - ModelProperty から派生させ、フォーム特有の項目だけ上書きする。
 */
export type FormProperty = Omit<
  ModelProperty,
  'name' | 'label' | 'type' | 'meta' | 'referenceTo' | 'selectOptions' | 'write'
> & {
  name?: string
  label?: string
  /** 型の項目で"type","meta"の組み合わせを処理する */
  typeData: FormPropertyTypeData | undefined
  referenceTo?: string
  selectOptions: Array<string | number> | undefined
  isExisting: boolean
}

const userOrGroupPermissionValueSchema = z.object({
  label: z.string(),
  value: z.string(),
  userType: z.enum(['user', 'group']),
  action: salescoreObjectPolicyActionSchema,
})

export const userOrGroupPermissionValueArraySchema = z.array(userOrGroupPermissionValueSchema)

export type UserOrGroupPermissionValue = z.infer<typeof userOrGroupPermissionValueSchema>

type PermissionUserAndGroupsKey = `${SalescoreObjectPolicyAction}PermissionUserAndGroups`

/** SalescoreModelForm 全体のフォーム値 */
export type SalescoreModelFormValue = {
  label?: string
  name?: string
  description?: string
  properties?: Array<FormProperty | undefined>
} & Partial<Record<PermissionUserAndGroupsKey, UserOrGroupPermissionValue[]>>

/** カスタムオブジェクトの"参照先"のオプションデータ */
export interface ReferenceOptionData {
  value: string
  key: string
  label: string
  model: CoreModel
}

/** 予約済みの内部名 */
const RESERVED_PROPERTY_NAMES = ['id', 'created_at', 'updated_at', 'created_by', 'updated_by']

/** 内部名の文字種規則: 先頭は小文字英字、以降は a-z 0-9 _ のみ */
export const INTERNAL_NAME_PATTERN = /^[a-z][a-z0-9_]*$/v

/** 内部名の入力規則 */
export const getNameRules = ({
  existingInternalNames,
  isExisting,
}: {
  existingInternalNames: string[]
  isExisting: boolean
}): Rule[] => [
  { required: true, message: t(`内部名を入力してください`) },
  {
    validator: async (_, value: string): Promise<Error | undefined> => {
      if (Array.contains(RESERVED_PROPERTY_NAMES, value)) {
        return await Promise.reject(new Error(t('内部名を「{{value}}」にすることはできません', { value })))
      }
    },
  },
  {
    // 新規項目のみ文字種をチェックする
    validator: async (_, value: string): Promise<Error | undefined> => {
      if (!isExisting && !isNullish(value) && value !== '' && !INTERNAL_NAME_PATTERN.test(value)) {
        return await Promise.reject(new Error())
      }
    },
    message: t('内部名は小文字英字で始まり、半角英数字とアンダースコアのみ使用できます'),
  },
  {
    // 既存の内部名と重複していないかをチェック
    // - 新規項目に対してのみ表示する
    // - PropertyDetailModalAndTrigger だとこのチェックは行われない（existingInternalNames の更新タイミングの都合）
    validator: async (_, value: string): Promise<Error | undefined> => {
      if (!isExisting && Array.countBy(existingInternalNames, (n) => n === value) > 1) {
        return await Promise.reject(new Error())
      }
    },
    message: t(`既に存在する内部名は入力できません`),
  },
]

/** "meta" が選択肢型かどうかをチェック */
export const isSelectMeta = (
  meta: unknown,
): meta is typeof PropertyMetaEnum.Select | typeof PropertyMetaEnum.MultiSelect =>
  PropertyMetaEnum.Select === meta || PropertyMetaEnum.MultiSelect === meta

/**
 * "selectOptions"の入力必須をチェック
 * - "選択肢(単数)" or "選択肢(複数)" なら、option の入力は必須
 */
export const isSelectOptionsEmpty = ({
  meta,
  selectOptions,
}: {
  meta: unknown
  selectOptions: Array<string | number> | undefined
}): boolean => isSelectMeta(meta) && (isNullish(selectOptions) || selectOptions.length === 0)
